// Port of PolylineCodec (CoreModels/Geo/GeometryCoding.swift): Google's encoded-polyline algorithm generalised to any
// integer precision. Packs use precision 6 for coordinates and a separate stream of elevations in decimetres.
//
// Values stay within ±2^53 for every input the packs contain (coordinates × 10^6, decimetres), so plain numbers are
// exact; chunks are combined with multiplication instead of 32-bit shifts for the same reason.

import type { GeoCoordinate, GeoPolygon, GeoPolyline } from './models';

export class PolylineCodecError extends Error {}

function decodeSigned(encoded: string): number[] {
  const values: number[] = [];
  let result = 0;
  let factor = 1;
  let inValue = false;
  for (let i = 0; i < encoded.length; i++) {
    const byte = encoded.charCodeAt(i);
    if (byte < 63 || byte >= 63 + 64) throw new PolylineCodecError('malformed');
    const chunk = byte - 63;
    result += (chunk & 0x1f) * factor;
    inValue = true;
    if (chunk < 0x20) {
      // zig-zag: odd = negative
      values.push(result % 2 === 1 ? -(result + 1) / 2 : result / 2);
      result = 0;
      factor = 1;
      inValue = false;
    } else {
      factor *= 32;
      if (factor > 2 ** 60) throw new PolylineCodecError('malformed');
    }
  }
  if (inValue) throw new PolylineCodecError('malformed');
  return values;
}

function appendSigned(value: number, out: string[]): void {
  let chunk = value < 0 ? -value * 2 - 1 : value * 2;
  while (chunk >= 0x20) {
    out.push(String.fromCharCode((0x20 | (chunk % 32)) + 63));
    chunk = Math.floor(chunk / 32);
  }
  out.push(String.fromCharCode(chunk + 63));
}

/** Decodes one delta-encoded stream of integers. */
export function decodeStream(encoded: string): number[] {
  let running = 0;
  return decodeSigned(encoded).map((delta) => (running += delta));
}

/** Delta-encodes one stream of integers. */
export function encodeStream(values: readonly number[]): string {
  const out: string[] = [];
  let previous = 0;
  for (const v of values) {
    appendSigned(v - previous, out);
    previous = v;
  }
  return out.join('');
}

export function decodeCoordinates(encoded: string, precision = 6): GeoCoordinate[] {
  const deltas = decodeSigned(encoded);
  if (deltas.length % 2 !== 0) throw new PolylineCodecError('malformed');
  const factor = 10 ** precision;
  const out: GeoCoordinate[] = new Array(deltas.length / 2);
  let lat = 0;
  let lon = 0;
  for (let i = 0, j = 0; i < deltas.length; i += 2, j++) {
    lat += deltas[i];
    lon += deltas[i + 1];
    out[j] = { latitude: lat / factor, longitude: lon / factor };
  }
  return out;
}

/** Swift `Double.rounded()`: to nearest, ties away from zero (`Math.round` sends -0.5 to 0 instead of -1). */
export function roundHalfAwayFromZero(x: number): number {
  return x < 0 ? -Math.round(-x) : Math.round(x);
}

export function encodeCoordinates(coordinates: readonly GeoCoordinate[], precision = 6): string {
  const factor = 10 ** precision;
  const out: string[] = [];
  let previousLat = 0;
  let previousLon = 0;
  for (const c of coordinates) {
    const lat = roundHalfAwayFromZero(c.latitude * factor);
    const lon = roundHalfAwayFromZero(c.longitude * factor);
    appendSigned(lat - previousLat, out);
    appendSigned(lon - previousLon, out);
    previousLat = lat;
    previousLon = lon;
  }
  return out.join('');
}

/** JSON shape of a polyline in a pack: either `points` or the compact `enc` (+ `ele` in decimetres, `p`). */
export interface PolylineJSON {
  points?: Array<{ coordinate: GeoCoordinate; elevation?: number | null }>;
  enc?: string;
  ele?: string;
  p?: number;
}

export function decodePolyline(json: PolylineJSON): GeoPolyline {
  if (json.points) {
    return {
      points: json.points.map((p) =>
        p.elevation === undefined || p.elevation === null
          ? { coordinate: p.coordinate }
          : { coordinate: p.coordinate, elevation: p.elevation },
      ),
    };
  }
  const coordinates = decodeCoordinates(json.enc ?? '', json.p ?? 6);
  if (json.ele !== undefined) {
    const decimetres = decodeStream(json.ele);
    if (decimetres.length !== coordinates.length) throw new PolylineCodecError('lengthMismatch');
    return { points: coordinates.map((coordinate, i) => ({ coordinate, elevation: decimetres[i] / 10 })) };
  }
  return { points: coordinates.map((coordinate) => ({ coordinate })) };
}

const coordinatePrecision = 6;

/**
 * Encodes a polyline like Swift `GeoPolyline.encode(to:)`: verbose `points` by default; with `compact`, `enc`/`p`
 * (+ `ele` in decimetres when every point has a finite elevation). Mixed elevations always fall back to `points`.
 */
export function encodePolyline(line: GeoPolyline, compact = false): PolylineJSON {
  const allElevations = line.points.every((p) => p.elevation !== undefined && Number.isFinite(p.elevation));
  const noElevations = line.points.every((p) => p.elevation === undefined);
  if (!compact || !(allElevations || noElevations)) {
    return {
      points: line.points.map((p) => {
        const coordinate = { latitude: p.coordinate.latitude, longitude: p.coordinate.longitude };
        return p.elevation === undefined ? { coordinate } : { coordinate, elevation: p.elevation };
      }),
    };
  }
  const json: PolylineJSON = {
    p: coordinatePrecision,
    enc: encodeCoordinates(
      line.points.map((p) => p.coordinate),
      coordinatePrecision,
    ),
  };
  if (allElevations && line.points.length > 0) {
    json.ele = encodeStream(line.points.map((p) => roundHalfAwayFromZero((p.elevation ?? 0) * 10)));
  }
  return json;
}

export interface PolygonJSON {
  exterior?: GeoCoordinate[];
  holes?: GeoCoordinate[][];
  enc?: string;
  encHoles?: string[];
  p?: number;
}

export function decodePolygon(json: PolygonJSON): GeoPolygon {
  if (json.exterior) return { exterior: json.exterior, holes: json.holes ?? [] };
  const precision = json.p ?? 6;
  return {
    exterior: decodeCoordinates(json.enc ?? '', precision),
    holes: (json.encHoles ?? []).map((h) => decodeCoordinates(h, precision)),
  };
}

/** Encodes a polygon like Swift `GeoPolygon.encode(to:)`: `exterior`/`holes`, or `enc`/`encHoles`/`p` with `compact`. */
export function encodePolygon(polygon: GeoPolygon, compact = false): PolygonJSON {
  const plain = (c: GeoCoordinate): GeoCoordinate => ({ latitude: c.latitude, longitude: c.longitude });
  if (!compact) return { exterior: polygon.exterior.map(plain), holes: polygon.holes.map((h) => h.map(plain)) };
  const json: PolygonJSON = { p: coordinatePrecision, enc: encodeCoordinates(polygon.exterior, coordinatePrecision) };
  if (polygon.holes.length > 0) json.encHoles = polygon.holes.map((h) => encodeCoordinates(h, coordinatePrecision));
  return json;
}
